// Uploads a file to an Azure Blob SAS URL. A single Put Blob is capped at 5000 MiB (~4.88 GiB) with
// the current service version, so files up to that go up in one PUT (the proven path); anything
// bigger — e.g. a 6+ GB scan video — must be split into blocks (Put Block + Put Block List) or Azure
// 413s.
const SINGLE_PUT_MAX = 4 * 1024 * 1024 * 1024;   // 4 GiB — safely under the ~4.88 GiB single-PUT ceiling
const BLOCK_SIZE = 100 * 1024 * 1024;            // 100 MiB per block (≤ 50,000 blocks → ~4.7 TB max)

export async function uploadToAzureBlobStorage(uploadUrl, file, contentType, onProgress = null) {
    if (file.size <= SINGLE_PUT_MAX) {
        return putSingle(uploadUrl, file, contentType, onProgress);
    }
    return putInBlocks(uploadUrl, file, contentType, onProgress);
}

// One PUT for the whole (small) file.
function putSingle(uploadUrl, file, contentType, onProgress) {
    return putXhr(
        uploadUrl,
        file,
        { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType },
        (loaded) => onProgress && onProgress(pct(loaded, file.size)),
    );
}

// Block upload: PUT each chunk as a block, then commit the block list.
async function putInBlocks(uploadUrl, file, contentType, onProgress) {
    const total = file.size;
    const count = Math.ceil(total / BLOCK_SIZE);
    const blockIds = [];
    let uploaded = 0;

    for (let i = 0; i < count; i++) {
        const start = i * BLOCK_SIZE;
        const end = Math.min(start + BLOCK_SIZE, total);
        const chunk = file.slice(start, end);

        // Block ids must be equal length before base64 — pad the index.
        const blockId = btoa(`block-${String(i).padStart(6, '0')}`);
        blockIds.push(blockId);

        const url = `${uploadUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;
        await putXhr(url, chunk, {}, (loaded) => onProgress && onProgress(pct(uploaded + loaded, total)));

        uploaded += (end - start);
        if (onProgress) onProgress(pct(uploaded, total));
    }

    // Commit the ordered block list; this is where the blob's content type is set.
    const body =
        `<?xml version="1.0" encoding="utf-8"?><BlockList>` +
        blockIds.map(id => `<Latest>${id}</Latest>`).join('') +
        `</BlockList>`;

    return putXhr(
        `${uploadUrl}&comp=blocklist`,
        body,
        { 'Content-Type': 'application/xml', 'x-ms-blob-content-type': contentType },
    );
}

function putXhr(url, body, headers = {}, onProgress = null) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);

        if (onProgress) {
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) onProgress(event.loaded);
            };
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve(true);
            else reject(new Error(xhr.responseText || `Upload failed: ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Upload failed.'));

        xhr.send(body);
    });
}

const pct = (loaded, total) => (total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0);
